/**
 * browserLaneRoster — which browser-lane connections a surface holds, and
 * whether a catalog row's app is one of them (2026-09-03 owner report).
 *
 * THE DEFECT THIS CLOSES
 * ----------------------
 * Onboarding read `getConnectedAccounts()` alone — Composio/OAuth connections.
 * A user who signed into Oura through Prest0n's browser was therefore told
 * "No integrations connected yet" on the readiness check, kept being nudged by
 * the connect coach, and kept seeing Oura in the recommended list. The missing
 * identity chip was the visible symptom of a page that did not know the
 * connection existed.
 *
 * THE KEYING PROBLEM
 * ------------------
 * The two namespaces do not line up. A catalog row is `ouraring`; its
 * browser-lane connection is `custom-oura_ring`; the user typed "Oura Ring".
 * `connectAppOfferKey` (connectApp.ts) is already THE match key for "is this
 * the same app?" across the connect lane — lowercase, strip everything but
 * letters and digits — so this reuses it rather than inventing a second
 * answer that could disagree with the first.
 *
 * Deliberately NOT resolved against the toolkit catalog: onboarding loads that
 * lazily for its apps phase, and this runs before it on the common path. A
 * catalog-dependent mapping would be correct only sometimes, which is worse
 * than not depending on it at all.
 *
 * Pure, so the rules can be executed by a test rather than inferred from a
 * 5,000-line page.
 */

import { connectAppOfferKey } from '../globalTools/tools/connectApp';
import type { CustomConnection } from './ComposioClient';

/** One browser-lane connection, as a surface needs it. */
export interface BrowserLaneEntry {
  /** The custom-connection slug — what an edit is recorded against. */
  slug: string;
  /** The name the user gave it ("Oura Ring"). */
  name: string;
  /** Which account signed in, when the user told us. Never guessed. */
  identity: string | null;
}

export interface BrowserLaneRoster {
  /** Keyed by match key, so a catalog slug can find it. */
  byKey: Record<string, BrowserLaneEntry>;
  /**
   * The slugs to add to a surface's connected list — ONE per connection.
   *
   * Never also the match key: a list carrying both would make onboarding's
   * readiness line say "2 integrations active" for a single connected app.
   */
  slugs: string[];
}

/**
 * Fold the custom-connection list into a roster.
 *
 * Only CONNECTED connections count. A 'registered' one is a sign-in that was
 * never finished and a 'signin-lost' one is a sign-in that has since gone —
 * counting either would tell the user they have an integration that does not
 * work, which is the failure mode the whole truth-verifier exists to prevent.
 */
export function buildBrowserLaneRoster(
  customs: ReadonlyArray<CustomConnection> | null | undefined,
): BrowserLaneRoster {
  const byKey: Record<string, BrowserLaneEntry> = {};
  const slugs: string[] = [];
  for (const c of customs || []) {
    if (!c || String(c.status || '') !== 'connected') continue;
    const slug = String(c.slug || '').trim();
    if (!slug) continue;
    const name = String(c.name || '').trim() || slug.replace(/^custom-/, '').replace(/_/g, ' ');
    // From the NAME first — that is what a catalog slug also collapses to.
    // The slug is the fallback for a connection registered without one.
    const key = connectAppOfferKey(name) || connectAppOfferKey(slug.replace(/^custom-/, ''));
    if (!key) continue;
    // First one wins: two connections collapsing to the same key is one app
    // connected twice, and the chip shows an account rather than a count.
    if (!byKey[key]) {
      byKey[key] = {
        slug,
        name,
        identity: typeof c.signedInAs === 'string' && c.signedInAs.trim()
          ? c.signedInAs.trim()
          : null,
      };
    }
    slugs.push(slug);
  }
  return { byKey, slugs };
}

/**
 * Is this catalog row's app connected — by either lane?
 *
 * `connectedApps.includes(slug)` only ever matched a Composio slug, so a
 * catalog row never lit up for a browser-lane connection to the same app.
 */
export function isAppConnectedInEitherLane(
  connectedApps: ReadonlyArray<string>,
  roster: BrowserLaneRoster | null | undefined,
  slug: string,
): boolean {
  const wanted = String(slug || '');
  if (!wanted) return false;
  if (connectedApps.includes(wanted)) return true;
  const key = connectAppOfferKey(wanted);
  return Boolean(key && roster?.byKey?.[key]);
}

/** The browser-lane entry behind a catalog slug, or null. */
export function browserLaneEntryFor(
  roster: BrowserLaneRoster | null | undefined,
  slug: string,
): BrowserLaneEntry | null {
  const key = connectAppOfferKey(String(slug || ''));
  if (!key) return null;
  return roster?.byKey?.[key] ?? null;
}
